import React from 'react'
import T from 'prop-types'
import IT from 'react-immutable-proptypes'
import {
  Canvas3D,
  Group3DFacade,
  ListFacade,
  PerspectiveCamera3DFacade,
  OrthographicCamera3DFacade,
} from 'troika-3d'
import {
  Vector3
} from 'three'
import memoize from 'memoize-one'
import StreamAreaD3 from './StreamAreaBasic'

const CAMERA_UP = new Vector3(0, -1, 0)

class TimelineCameraPerspective extends PerspectiveCamera3DFacade {
  constructor(...args) {
    super(...args)
    this.fov = 60
    this.near = 0.1
    this.far = 1e10
  }

  afterUpdate() {
    this.z = this.zOffset + (this.distance * Math.sin(this.angle))
    this.y = this.yOffset + (this.elevation)
    this.x = this.xOffset + (this.distance * Math.cos(this.angle))
    super.afterUpdate()
  }
}


class TimelineImpl3D extends React.PureComponent {
  static displayName = "Schemer.TimelineImpl3D"

  static propTypes = {
    height: T.number,
    width: T.number,

    xScale: T.any,
    yScale: T.any,
    colorScale: T.any,
    minX: T.number,
    maxX: T.number,
    minY: T.number,
    maxY: T.number,
    renderData: IT.list,

    xFields: T.array,
    xFieldKeys: T.arrayOf(T.string),
    yFields: T.array,
    yFieldKeys: T.arrayOf(T.string),
    yFieldKeysToStyles: T.object,
    colorFields: T.array,
    colorFieldKeys: T.arrayOf(T.string),
    activeXFieldKey: T.string,
    activeYFieldKeys: T.string,
    activeColorFieldKey: T.string,

    selectedXFieldKey: T.string,
    selectedYFieldKeys: T.string,
    selectedColorFieldKey: T.string,

    pathInterpolate: T.string,
    xExponent: T.number,

    minYForceZero: T.bool,

    onPathMouseOver: T.func,
    onPathMouseOut: T.func,
    onPathMouseMove: T.func,
    highlightedPathKey: T.string,
  }

  constructor(props) {
    super(props)
    this.state = {
      enableCameraSpin: true,
      cameraDistance: 110, //ORTHO_CAMERA_INITIAL ? 0 : 40,
      cameraElevation: -20,//ORTHO_CAMERA_INITIAL ? 0 : 50,
      cameraAngle: 4.69, //ORTHO_CAMERA_INITIAL ? 0 : 50,
      cameraLookAt: {x:0, y:0, z:0}
    }
  }


  getCamera = memoize((is3D, height, width, cameraElevation, cameraAngle, cameraDistance) => {
    return is3D ? {
      facade: TimelineCameraPerspective,
      x: width / 2,
      y: height / 2,
      z: -100,
      fov: 120,
      xOffset: width / 2,
      yOffset: height / 2,
      zOffset: 0,
      elevation: cameraElevation,
      angle: cameraAngle,
      distance: cameraDistance,
      lookAt: new Vector3(width / 2, height / 2, 0),
      up: CAMERA_UP,
      // transition: true ? {
      //   angle: 1,
      //   distance: 1,
      //   elevation: 1
      // } : null,
      // animation: false ? {
      //   0: {
      //     angle: cameraAngle
      //   },
      //   100: {
      //     angle: cameraAngle + Math.PI * 2
      //   },
      //   duration: 30000,
      //   iterations: Infinity
      // } : null
    } : {
      facade: OrthographicCamera3DFacade,
      z: 1,
      top: 0,
      bottom: height,
      left: 0,
      right: width,
      near: 100,
      far: -100
    }
  })

  render() {
    const {
      height,
      width,

      xScale,
      yScale,
      colorScale,
      minX,
      maxX,
      minY,
      maxY,
      renderData,

      yFieldKeysToStyles,

      pathInterpolate,
      xExponent
    } = this.props
    const {
      cameraDistance,
      cameraAngle,
      cameraElevation,
      is3D
    } = this.state

    if (!renderData || !height || !width) {
      return <div className="schemer_timeline no_data" />
    }

    const yZero = yScale(0)

    return (
      <Canvas3D
        height={ height }
        width={ width }
        antialias
        backgroundColor={ null }
        lights={ [
          { type: 'ambient', color: 0xffffff },
          { type: 'point', color: 0xffffff }
        ] }
        camera={this.getCamera(is3D, height, width, cameraElevation, cameraAngle, cameraDistance)}
        objects={ [
          {
            key: 'main',
            facade: Group3DFacade,
            x: 0,
            y: 0,
            z: 0,
            children: [
              {
                key: 'mainGraph',
                facade: ListFacade,
                data: renderData,
                template: {
                  facade: StreamAreaD3,

                  width: width,
                  height: height,
                  isPerspectiveCamera: is3D, // Needed for meshLine width bug that is dependent upon this

                  x: 0,
                  y: yZero, // Will ba animated in (once)
                  z: s => s.z,
                  scaleY: 0.0, // Will be animated in (once)

                  key: s => `timeline_item_${ s.name }`,
                  name: s => s.name,
                  values: s => s.values,

                  fillColor: s => yFieldKeysToStyles.getIn([s.dataKey, 'fillColor']),
                  strokeColor: s => yFieldKeysToStyles.getIn([s.dataKey, 'strokeColor']),
                  strokeWidth: s => yFieldKeysToStyles.getIn([s.dataKey, 'strokeWidth']),
                  strokeStyle: s => yFieldKeysToStyles.getIn([s.dataKey, 'strokeStyle']),

                  dashRatio: s => yFieldKeysToStyles.getIn([s.dataKey, 'dashRatio']),
                  dashArray: s => yFieldKeysToStyles.getIn([s.dataKey, 'dashArray']),
                  //dashOffset: s => yFieldKeysToStyles.getIn([s.dataKey, 'dashOffset']), // Also animated...
                  dashOffset: 0.0, // Will be animated if this is a dashed line

                  //onMouseOver: this.handleLineMouseOver,
                  //onMouseOut: this.handleLineMouseOut,
                  //onMouseOver: () => this._handleMainMouseMove,
                  //onMouseOut: () => this._handleMainClick,
                  //pointerEvents: true,

                  xScale: () => xScale,
                  yScale: () => yScale,
                  colorScale: () => colorScale,

                  pathInterpolate,
                  minX,
                  maxX,
                  minY,
                  maxY,
                  xExponent,

                  /*
                  animation: (d, i) => ([
                    {
                      0: {
                        scaleY: 0.001
                      },
                      100: {
                        scaleY: 1.0
                      },
                      easing: 'easeOutExpo',
                      duration: 1000,
                      iterations: 1,
                    },
                    {
                      0: {
                        y: yZero
                      },
                      100: {
                        y: 0
                      },
                      easing: 'easeOutExpo',
                      duration: 1000,
                      iterations: 1,
                    },
                    {
                      0: {
                        z: 0.001
                      },
                      100: {
                        z: 2 * d.z
                      },
                      duration: 100,
                      iterations: 1,
                      delay: 1000 + i * 50
                    },
                    {
                      0: {
                        dashOffset: 0.000
                      },
                      100: {
                        dashOffset: yFieldKeysToStyles.getIn([d.dataKey, 'dashArray']) // TODO should be dashArray value?
                      },
                      duration: 2000,
                      iterations: Infinity,
                      delay: 0
                    },
                  ]),*/
                  /*transition: {
                    values: {
                      duration: 1000,
                      easing: 'easeOutExpo',
                      interpolate: interpolateTimelineValues
                    },
                    minX: {
                      duration: transitionDuration,
                      easing: transitionType === 'play' ? 'linear' : 'easeOutExpo'
                    },
                    maxX: {
                      duration: transitionDuration,
                      easing: transitionType === 'play' ? 'linear' : 'easeOutExpo'
                    }
                  }*/
                }
              }
            ]
          }
        ] }
      />
    )
  }
}

export default TimelineImpl3D
